import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiError } from '@aithora/shared';

export interface QueryState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
  /** Replace the data locally (optimistic updates). */
  setData: (updater: (prev: T | null) => T | null) => void;
}

/**
 * Load something from the API, optionally re-loading every `pollMs`. A failed poll
 * keeps the last good data on screen and reports the error beside it.
 */
export function useQuery<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[], opts: { pollMs?: number; enabled?: boolean } = {}): QueryState<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(opts.enabled !== false);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (opts.enabled === false) {
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    loadRef
      .current(ctrl.signal)
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setDataState(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted || (e instanceof DOMException && e.name === 'AbortError')) return;
        setError(e as ApiError);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, opts.enabled]);

  useEffect(() => {
    if (!opts.pollMs || opts.enabled === false) return;
    const id = window.setInterval(() => setTick((t) => t + 1), opts.pollMs);
    return () => window.clearInterval(id);
  }, [opts.pollMs, opts.enabled]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setData = useCallback((u: (prev: T | null) => T | null) => setDataState((p) => u(p)), []);
  return { data, error, loading, reload, setData };
}
